/**
 * Quick layouts — the ready-made ways of filling a bare area of a surface in
 * one go, offered on the "+" of every bare area in the designer.
 *
 * Each is a stack of horizontal BANDS, top to bottom, each band a product in a
 * colour with a share of the area's height. `quickLayoutSections` turns one
 * into real sections for a given bare area:
 *   - a SlatWall band is whole 6" rows tall (its share, rounded DOWN to rows,
 *     never fewer than one);
 *   - the Wall&CeilingBoard bands share whatever height is left, in proportion
 *     to their shares, so the stack always fills the area exactly;
 *   - every band must still be at least the minimum section size, or the
 *     layout does not fit this area (null — the designer greys it out).
 *
 * The bands touch: SlatWall over boards above and below is the normal
 * stacked-band wainscot, and the trim rules handle the joins.
 *
 * The colours named here are what the layout ASKS for. The designer still
 * substitutes what the retailer carries (see sectionOptions.pickColor).
 */

import { PanelOrientation, ProductColor } from '../../types';
import { Section, SectionProductType, Surface } from '../../types/sections';
import { SLATWALL_COVERAGE_HEIGHT } from '../constants';
import { Rect } from './resolver';
import { MIN_SECTION_SIZE_FT } from './validation';
import { nextSectionId } from './edits';
import { outlineTopRange, surfaceOutline } from './outline';

export type QuickLayoutId = 'single' | 'halves' | 'slatwall-band-white' | 'slatwall-band-gray';

export interface QuickLayoutBand {
  productType: SectionProductType;
  color: ProductColor;
  /** Wall&CeilingBoard only. */
  orientation?: PanelOrientation;
  /** Share of the area's height, relative to the other bands (e.g. 1, 1, 1). */
  share: number;
}

export interface QuickLayout {
  id: QuickLayoutId;
  /** Top to bottom. */
  bands: QuickLayoutBand[];
}

const WCB = 'WCB' as const;
const SLATWALL = 'SlatWall' as const;

/**
 * The layouts, in the order they are offered. `single` is the plain fill the
 * "+" always did — one section of the surface's usual product, matching what
 * is already there; the designer builds that one itself (sectionForGap).
 */
export const QUICK_LAYOUTS: QuickLayout[] = [
  { id: 'single', bands: [{ productType: WCB, color: 'White', orientation: 'Horizontal', share: 1 }] },
  {
    id: 'halves',
    bands: [
      { productType: WCB, color: 'White', orientation: 'Vertical', share: 1 },
      { productType: WCB, color: 'Gray', orientation: 'Horizontal', share: 1 },
    ],
  },
  {
    id: 'slatwall-band-white',
    bands: [
      { productType: WCB, color: 'White', orientation: 'Vertical', share: 1 },
      { productType: SLATWALL, color: 'Gray', share: 1 },
      { productType: WCB, color: 'White', orientation: 'Vertical', share: 1 },
    ],
  },
  {
    id: 'slatwall-band-gray',
    bands: [
      { productType: WCB, color: 'Gray', orientation: 'Vertical', share: 1 },
      { productType: SLATWALL, color: 'White', share: 1 },
      { productType: WCB, color: 'Gray', orientation: 'Vertical', share: 1 },
    ],
  },
];

const EPS = 1e-6;

/** Whole 6" rows that fit in `height` (at least one). */
function wholeRows(height: number): number {
  return Math.max(1, Math.floor(height / SLATWALL_COVERAGE_HEIGHT + EPS)) * SLATWALL_COVERAGE_HEIGHT;
}

/**
 * The height of the area the bands are shared over: on a plain wall the whole
 * area; on a wall under a sloped ceiling (a gable or shed end) only the LEVEL
 * part — up to where the wall's top starts to rise, i.e. the height of the
 * walls either side of it. So a SlatWall band lands at the same height and
 * size on every wall of the room, and the top band runs on up into the peak.
 * (An area that starts above the level part is shared in full.)
 */
export function quickLayoutLevelHeight(surface: Surface, gap: Rect): number {
  const outline = surfaceOutline(surface);
  if (!outline) return gap.height;
  const level = outlineTopRange(outline, 0, surface.width).min - gap.y;
  return level > MIN_SECTION_SIZE_FT ? Math.min(gap.height, level) : gap.height;
}

/** The height each band gets in an area `height` tall (top to bottom), or null if one would be too small. */
export function quickLayoutHeights(bands: QuickLayoutBand[], height: number): number[] | null {
  const totalShare = bands.reduce((t, b) => t + b.share, 0);
  if (totalShare <= 0) return null;
  // SlatWall first: it must be whole rows, so it takes its share rounded to rows…
  const heights = bands.map((b) => (b.productType === SLATWALL ? wholeRows((height * b.share) / totalShare) : 0));
  const slatHeight = heights.reduce((t, h) => t + h, 0);
  // …and the boards share what is left.
  const boardShare = bands.filter((b) => b.productType !== SLATWALL).reduce((t, b) => t + b.share, 0);
  const left = height - slatHeight;
  bands.forEach((b, i) => {
    if (b.productType !== SLATWALL) heights[i] = (left * b.share) / boardShare;
  });
  return heights.every((h) => h >= MIN_SECTION_SIZE_FT - EPS) ? heights : null;
}

/**
 * The sections a layout makes in a bare area of `surface`, stacked to fill it
 * (bottom band at the bottom of the area). `colorFor` says which colour to use
 * in place of each the layout asks for — what the retailer carries. Null when
 * the layout does not fit the area.
 */
export function quickLayoutSections(
  surface: Surface,
  gap: Rect,
  layout: QuickLayout,
  colorFor: (type: SectionProductType, wanted: ProductColor) => ProductColor | null,
): Section[] | null {
  const heights = quickLayoutHeights(layout.bands, quickLayoutLevelHeight(surface, gap));
  if (!heights) return null;
  // Whatever rises above the level part (the peak) goes to the top band.
  heights[0] += gap.height - heights.reduce((t, h) => t + h, 0);
  const sections: Section[] = [];
  // Ids are handed out against the surface as it grows, so the bands never share one.
  let growing: Surface = surface;
  let y = gap.y;
  for (let i = layout.bands.length - 1; i >= 0; i--) {
    const band = layout.bands[i];
    const color = colorFor(band.productType, band.color);
    if (!color) return null;
    const id = nextSectionId(growing, band.productType);
    const section: Section = band.productType === SLATWALL
      ? { id, productType: SLATWALL, x: gap.x, y, width: gap.width, height: heights[i], color }
      : { id, productType: WCB, x: gap.x, y, width: gap.width, height: heights[i], color, orientation: band.orientation ?? 'Horizontal' };
    sections.push(section);
    growing = { ...growing, sections: [...growing.sections, section] };
    y += heights[i];
  }
  return sections;
}
